import { useState, type ReactElement, type ReactNode } from 'react'
import { ResponsiveContainer } from 'recharts'

import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

/** Shared Recharts styling (Shopify Analytics): solid hairline horizontal grid, no axis lines, 12px grey ticks. */
export const chartAxis = {
  axisLine: false,
  tickLine: false,
  tick: { fill: 'hsl(var(--chart-axis))', fontSize: 12 },
} as const

export const chartGrid = { vertical: false, stroke: 'hsl(var(--chart-grid))' } as const

/** 12500 -> 12.5k */
export const compactNumber = (n: number) =>
  new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n)

export function ChartTooltip({
  active,
  payload,
  label,
  format,
}: {
  active?: boolean
  payload?: ReadonlyArray<{ value?: unknown; name?: unknown; color?: string }>
  label?: unknown
  format: (v: number) => string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg bg-popover px-3 py-2 shadow-dropdown">
      {label !== undefined && <p className="text-xs text-muted-foreground">{String(label)}</p>}
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-2 text-sm font-semibold tabular-nums text-ink">
          <span className="size-2 rounded-full" style={{ background: p.color }} aria-hidden="true" />
          {format(Number(p.value))}
        </p>
      ))}
    </div>
  )
}

export interface LegendItem {
  label: string
  value: ReactNode
  color: string
}

/** Legend row: a dot, the series name and its value, as Shopify's legend under a chart. */
export function ChartLegend({ items }: { items: LegendItem[] }) {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2">
      {items.map((i) => (
        <p key={i.label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="size-2 rounded-full" style={{ background: i.color }} aria-hidden="true" />
          {i.label}
          <span className="font-medium tabular-nums text-ink">{i.value}</span>
        </p>
      ))}
    </div>
  )
}

interface ChartCardProps {
  title: string
  /** The headline number under the title ("BDT 12,400"). */
  value?: ReactNode
  subheader?: ReactNode
  action?: ReactNode
  legend?: ReactNode
  /** Below the chart (e.g. a donut's breakdown). */
  footer?: ReactNode
  /** Same numbers as a table, readable without the chart. */
  table?: { columns: [string, string]; rows: [string, string][] }
  empty?: boolean
  emptyText?: string
  height?: number
  className?: string
  children: ReactElement
}

/** Shopify analytics card: dotted-underlined title, headline value, the chart, and a "Show table" alternative. */
export function ChartCard({ title, value, subheader, action, legend, footer, table, empty, emptyText = 'No data for this period yet.', height = 320, className, children }: ChartCardProps) {
  const [asTable, setAsTable] = useState(false)
  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div className="min-w-0">
          <CardTitle className="w-fit text-sm font-semibold underline decoration-faint decoration-dotted underline-offset-4">{title}</CardTitle>
          {value !== undefined && <p className="mt-1 text-heading-lg tabular-nums text-ink">{value}</p>}
          {subheader && <CardDescription className="mt-1 text-xs">{subheader}</CardDescription>}
        </div>
        <div className="flex items-center gap-1">
          {action}
          {table && !empty && (
            <Button variant="link" size="sm" onClick={() => setAsTable((t) => !t)} aria-pressed={asTable}>
              {asTable ? 'Show chart' : 'Show table'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {legend && <div className="mb-4">{legend}</div>}
        {empty ? (
          <div className="grid place-items-center text-sm text-muted-foreground" style={{ height }}>
            {emptyText}
          </div>
        ) : asTable && table ? (
          <div className="overflow-y-auto" style={{ maxHeight: height }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-strong bg-subdued text-left text-xs text-text-2">
                  <th className="px-2 py-2 font-medium">{table.columns[0]}</th>
                  <th className="px-2 py-2 text-right font-medium">{table.columns[1]}</th>
                </tr>
              </thead>
              <tbody>
                {table.rows.map(([a, b]) => (
                  <tr key={a} className="border-b border-border-subtle [&>td]:px-2">
                    <td className="py-1.5">{a}</td>
                    <td className="py-1.5 text-right tabular-nums">{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ height }}>
            <ResponsiveContainer width="100%" height="100%">
              {children}
            </ResponsiveContainer>
          </div>
        )}
        {footer && !empty && !asTable && <div className="mt-4">{footer}</div>}
      </CardContent>
    </Card>
  )
}
